import { getAlertsSummaryData } from '@src/api/aiAlerts';
import useAiAlertStore from '@src/globalStore/aiAlertStore';
import { dimensionsListSelectorKeyValue } from '@src/store/common/dimensionList/selectors';
import { aiAlertsDimensionMappingSelector } from '@src/store/common/selectors';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import { useAtomValue } from 'jotai';
import { get } from 'lodash';
import moment from 'moment-timezone';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { searchTextAtom } from '../atoms';
import { formatAIAlertSummary } from '../formatter';

export const useFetchAlerts = (interval) => {
  const { from, to } = interval;
  const dimensionListObject = useSelector(dimensionsListSelectorKeyValue);
  const dimensionMapping = useSelector(aiAlertsDimensionMappingSelector);
  const searchText = useAtomValue(searchTextAtom);
  const { autoInsightsFilter } = useAiAlertStore((state) => ({
    autoInsightsFilter: state.autoInsightsFilter,
  }));

  const {
    data: alerts,
    isFetching,
    isError,
    refetch,
  } = useQuery(
    ['ai-alerts-list', interval],
    () => {
      return getAlertsSummaryData({
        params: {
          from: moment(from).format(),
          to: moment(to).format(),
          cache: true,
        },
      }).then((response) => {
        const data = get(response, ['data'], {});
        return data;
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 60 * 1000, // 1m
      enabled: !!interval && !!to && !!from,
    },
  );

  const result = alerts?.length
    ? formatAIAlertSummary(
        alerts,
        dimensionListObject as any,
        dimensionMapping[CURRENT_PRODUCT_MODE.toLowerCase()],
      )
    : [];

  // Filter data based on auto insights filter if applied
  const filteredResult = autoInsightsFilter.alertIds?.length
    ? result.filter((alert) => {
        const alertIdSet = new Set(autoInsightsFilter.alertIds);
        return alertIdSet.has(alert.alertId);
      })
    : result;

  // Filter data based on search text
  const searchedResult = filteredResult.filter((record) => {
    // Search across Alert ID, Root Cause, Metric Name
    const searchFields = [
      record.alertId.toString(),
      record.rootCauseDisplay,
      record.metricDisplayName,
    ];

    return searchFields.some((field) =>
      field.toString().toLowerCase().includes(searchText.toLowerCase()),
    );
  });

  return {
    alerts: searchedResult,
    isEmpty: searchedResult?.length === 0,
    isFetching,
    isError,
    refetch,
  };
};
